import React, { useState, useEffect } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Upload, X } from 'lucide-react';
import { useAudience } from '../../../context/AudienceContext';
import { useImageDraft } from './useImageDraft';
import {
  MAX_UPLOAD_SIZE_MB,
  ALLOWED_IMAGE_MIME_TYPES,
  ALLOWED_IMAGE_EXTENSIONS_RE,
  ACCEPTED_IMAGE_INPUT,
  UPLOAD_HINT_TEXT,
} from './conceptImageFormats';

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface InsightsFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  dynamicSegmentationId?: number | null;
  dynamicSegmentIndices?: number[];
}

interface Segment {
  id: number;
  name: string;
  description: string;
  len: number;
  created_at: string;
  updated_at: string;
}

type ImageFile = {
  id: string;
  file: File;
  preview: string;
};

const API_URL = import.meta.env.VITE_API_URL || '';

const LogoTest: React.FC<InsightsFormProps> = ({ 
  onSubmit, 
  selectedSegmentIds, 
  personaFilters, 
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  dynamicSegmentationId,
  dynamicSegmentIndices = [],
}) => {
  const { audienceData } = useAudience();
  const [_segments, setSegments] = useState<Segment[]>([]);
  
  // Initialize form state from initialFormData
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [goal, setGoal] = useState(initialFormData.goal || '');
  // Images picked in THIS sitting, handed back on a Back-and-return. Read as a state
  // SEED so they are present on the first painted frame: this form gates its own
  // Continue on images.length, so a one-frame gap flashes the button disabled.
  const { imageDraftKey, restored, persistImages } = useImageDraft(initialFormData);
  const [images, setImages] = useState<ImageFile[]>(() => (restored?.images as ImageFile[]) || []);
  // Seeded from the draft alongside `images` — the current logo is a separate slot, and
  // leaving it out would restore the concepts while silently dropping the logo they are
  // being compared against.
  const [currentLogo, setCurrentLogo] = useState<ImageFile | null>(
    () => ((restored?.currentLogo as ImageFile[]) || [])[0] || null,
  );
  
  const [_loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const currentLogoInputRef = React.useRef<HTMLInputElement>(null);
  // Draft images are the freshest thing we have, so the server-path restore below
  // (which rebuilds zero-byte Files from image_paths) must not overwrite them.
  const imagesLoadedRef = React.useRef(!!restored?.images);
  
  // Update parent component with form data changes
  useEffect(() => {
    const formData = {
      simName,
      goal,
      // imageDraftKey resolves to the concepts AND the current logo via imageDraftStore.
      // They stay out of the draft itself on purpose: a File is not JSON and a blob: URL
      // dies with its document.
      imageDraftKey,
    };

    onFormDataChange(formData);
  }, [simName, goal, imageDraftKey]); // Removed onFormDataChange to prevent infinite loop
  
  // Mirror the picked images into the in-memory store the draft key points at.
  useEffect(() => {
    persistImages({ images, currentLogo: currentLogo ? [currentLogo] : [] });
  }, [images, currentLogo, persistImages]);

  // Initialize images from initialFormData (for Edit & Rerun)
  useEffect(() => {
    if (imagesLoadedRef.current) return;
    
    const loadImages = async () => {
      // Load logo concept images
      const initialImages = initialFormData.images || [];
      if (initialImages.length > 0) {
        const loadedImages: ImageFile[] = initialImages.map((src: string, index: number) => ({
          id: `existing-${index}-${Date.now()}`,
          file: new File([], `existing-logo-${index}.png`, { type: 'image/png' }),
          preview: src.startsWith('data:') || src.startsWith('http') ? src : `${API_URL}${src}`
        }));
        setImages(loadedImages);
      }
      
      // Load current logo
      const initialCurrentLogo = initialFormData.currentLogo;
      if (initialCurrentLogo) {
        const logoPreview = initialCurrentLogo.startsWith('data:') || initialCurrentLogo.startsWith('http') 
          ? initialCurrentLogo 
          : `${API_URL}${initialCurrentLogo}`;
        setCurrentLogo({
          id: `current-existing-${Date.now()}`,
          file: new File([], 'existing-current-logo.png', { type: 'image/png' }),
          preview: logoPreview
        });
      }
      
      imagesLoadedRef.current = true;
    };
    
    if ((initialFormData.images?.length > 0 || initialFormData.currentLogo) && !imagesLoadedRef.current) {
      loadImages();
    }
  }, [initialFormData.images, initialFormData.currentLogo]);
  
  // NOTE: previews are deliberately NOT revoked on unmount. They are exactly what
  // imageDraftStore is holding for the Back-and-return, and revoking them here
  // restores dead thumbnails — worse than restoring nothing. Removal revokes.
  
  // Handle field changes and notify parent about edits
  const handleFieldChange = (
    setter: React.Dispatch<React.SetStateAction<string>>, 
    value: string
  ) => {
    setter(value);
    if (onEditStep) {
      onEditStep();
    }
  };
  
  // Image handling functions
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    // Only allow the shared Gemini-supported image formats within the size cap
    const validFiles = Array.from(files).filter(file => {
      const isSupportedType =
        ALLOWED_IMAGE_MIME_TYPES.includes(file.type) || ALLOWED_IMAGE_EXTENSIONS_RE.test(file.name);
      if (!isSupportedType) {
        alert(`"${file.name}" isn't a supported image. ${UPLOAD_HINT_TEXT}`);
        return false;
      }
      if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
        alert(`"${file.name}" is too large. Maximum size is ${MAX_UPLOAD_SIZE_MB}MB.`);
        return false;
      }
      return true;
    });
    
    if (validFiles.length === 0) return;
    
    // Check if adding these files would exceed the 5-image limit
    const remainingSlots = 5 - images.length;
    if (remainingSlots <= 0) {
      alert('Maximum of 5 images allowed. Please remove some images first.');
      return;
    }
    
    const filesToAdd = validFiles.slice(0, remainingSlots);
    if (validFiles.length > remainingSlots) {
      alert(`Only ${remainingSlots} more images can be added. ${filesToAdd.length} images will be uploaded.`);
    }
    
    const newImages = filesToAdd.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file)
    }));
    
    setImages([...images, ...newImages]);
    if (onEditStep) {
      onEditStep();
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const removeImage = (imageId: string) => {
    const imageIndex = images.findIndex(img => img.id === imageId);
    if (imageIndex === -1) return;
    
    const imageToRemove = images[imageIndex];
    
    // Create new array without the image
    const newImages = images.filter(img => img.id !== imageId);
    setImages(newImages);
    
    // Only revoke blob URLs, not http/data URLs
    if (imageToRemove.preview.startsWith('blob:')) {
      setTimeout(() => {
        URL.revokeObjectURL(imageToRemove.preview);
      }, 0);
    }
    
    if (onEditStep) {
      onEditStep();
    }
  };
  
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  
  const handleCurrentLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Only allow the shared Gemini-supported image formats within the size cap
    const isSupportedType =
      ALLOWED_IMAGE_MIME_TYPES.includes(file.type) || ALLOWED_IMAGE_EXTENSIONS_RE.test(file.name);
    if (!isSupportedType) {
      alert(`"${file.name}" isn't a supported image. ${UPLOAD_HINT_TEXT}`);
      if (currentLogoInputRef.current) {
        currentLogoInputRef.current.value = '';
      }
      return;
    }
    if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) {
      alert(`"${file.name}" is too large. Maximum size is ${MAX_UPLOAD_SIZE_MB}MB.`);
      if (currentLogoInputRef.current) {
        currentLogoInputRef.current.value = '';
      }
      return;
    }
    
    // Remove old current logo if exists
    if (currentLogo) {
      URL.revokeObjectURL(currentLogo.preview);
    }
    
    const newCurrentLogo = {
      id: `current-${Date.now()}`,
      file,
      preview: URL.createObjectURL(file)
    };
    
    setCurrentLogo(newCurrentLogo);
    if (onEditStep) {
      onEditStep();
    }
    
    // Reset file input
    if (currentLogoInputRef.current) {
      currentLogoInputRef.current.value = '';
    }
  };
  
  const removeCurrentLogo = () => {
    if (currentLogo) {
      // Only revoke blob URLs, not http/data URLs
      if (currentLogo.preview.startsWith('blob:')) {
        URL.revokeObjectURL(currentLogo.preview);
      }
      setCurrentLogo(null);
      if (onEditStep) {
        onEditStep();
      }
    }
  };
  
  const triggerCurrentLogoInput = () => {
    if (currentLogoInputRef.current) {
      currentLogoInputRef.current.click();
    }
  };
  
  useEffect(() => {
    const fetchSegments = async () => {
      if (!audienceData.audienceId) return;
      
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/audience/${audienceData.audienceId}/segments`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch segments');
        }
        
        const data = await response.json();
        // Filter segments to only include the selected ones
        const filteredSegments = data.filter((segment: Segment) => 
          selectedSegmentIds.includes(segment.id)
        );
        setSegments(filteredSegments);
        setError(null);
      } catch (err) {
        console.error('Error fetching segments:', err);
        setError('Failed to load segments. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchSegments();
  }, [audienceData.audienceId, selectedSegmentIds]);
  
    // Edit & Rerun of a sim that used DYNAMIC segmentation passes these instead of
  // segment_ids, and contentData.segment_ids is [] for such a sim. These forms did not
  // accept the props, so isFormValid's `selectedSegmentIds.length > 0` never became true
  // and Run stayed disabled forever, with the dynamic scope lost (#468 F3-03). Same shape
  // SurveyAndFocusGroups already uses.
  const isFormValid = (selectedSegmentIds.length > 0 || dynamicSegmentIndices.length > 0) && simName.trim() !== '' && goal.trim() !== '' && images.length > 0;
  
  // Utility function to convert an image to base64
  // Handles both File objects and existing URLs/base64 strings
  const imageToBase64 = async (img: ImageFile): Promise<string> => {
    // If preview is already base64, use it directly
    if (img.preview.startsWith('data:')) {
      return img.preview;
    }
    
    // If it's a URL (existing image from server), fetch and convert
    if (img.preview.startsWith('http') || img.preview.startsWith('/')) {
      try {
        const response = await fetch(img.preview, { credentials: 'include' });
        const blob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        console.error('Error fetching image:', error);
        // Return the URL as-is if fetch fails
        return img.preview;
      }
    }
    
    // Otherwise convert the file (for newly uploaded images)
    if (img.file.size > 0) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(img.file);
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = error => reject(error);
      });
    }
    
    // Fallback to preview
    return img.preview;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    
    setIsSubmitting(true);
    
    try {
      // Convert all images to base64 (handles both existing and new images)
      const imagePromises = images.map(image => imageToBase64(image));
      const base64Images = await Promise.all(imagePromises);
      
      // Convert current logo to base64 if it exists
      let currentLogoBase64 = null;
      if (currentLogo) {
        currentLogoBase64 = await imageToBase64(currentLogo);
      }
      
      // Prepare the simulation data
      const simulationData = {
        audience_id: audienceData.audienceId,
        segment_ids: selectedSegmentIds,
        // Carry the dynamic scope through, or unlocking Run above would just let the
        // user re-run against nothing (#468 F3-03).
        ...(dynamicSegmentationId
          ? { dynamic_segmentation_id: dynamicSegmentationId, dynamic_segment_indices: dynamicSegmentIndices }
          : {}),
        name: simName,
        persona_filters: personaFilters,
        task: 'logo_test',
        goal: goal,
        images: base64Images,
        current_logo: currentLogoBase64, // Add current logo for reference
      };
      
      // Send the request to start a simulation
      const response = await fetch(`${API_URL}/simulations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(simulationData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to start simulation');
      }
      
      const data = await response.json();
      
      // Call onSubmit with the simulation ID
      onSubmit(data.simulation_id);
      
    } catch (err) {
      console.error('Error starting simulation:', err);
      setError('Failed to start simulation. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
          <span className="text-sm text-gray-500">{selectedSegmentIds.length} segments selected</span>
        </div>
        
        {error ? (
          <div className="text-sm text-red-600 mb-3">
            {error}
          </div>
        ) : null}
      </div>

      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name
        </label>
        <input
          id="simName"
          type="text"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          value={simName}
          onChange={(e) => handleFieldChange(setSimName, e.target.value)}
          placeholder="Enter a name for your logo test simulation"
        />
      </div>
      
      <div className="mb-6">
        <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
          Goal
        </label>
        <textarea
          id="goal"
          className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          rows={4}
          value={goal}
          onChange={(e) => handleFieldChange(setGoal, e.target.value)}
          placeholder="What do you want to learn from testing these logos? (e.g., Which logo best represents our brand values, which generates more trust, etc.)"
        />
      </div>
      
      {/* Current Logo Section (Optional Reference) */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Current Logo (Optional - For Comparison)
          </label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={triggerCurrentLogoInput}
            aria-label="Upload current logo"
            title="Upload current logo"
          >
            <Upload className="w-4 h-4 mr-1" /> Upload Current Logo
          </Button>
          <input
            type="file"
            ref={currentLogoInputRef}
            onChange={handleCurrentLogoUpload}
            accept={ACCEPTED_IMAGE_INPUT}
            className="hidden"
            aria-label="Upload current logo for reference"
            title="Upload current logo for reference"
          />
        </div>
        
        <p className="text-xs text-gray-500 mb-3">
          Upload your current/existing logo to help personas compare the new concepts against what they know
        </p>
        
        {!currentLogo ? (
          <div 
            className="border-2 border-dashed border-blue-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 transition-colors bg-blue-50/20"
            onClick={triggerCurrentLogoInput}
          >
            <Upload className="w-6 h-6 mx-auto text-blue-400" />
            <p className="mt-1 text-xs text-blue-600">
              Click to upload your current logo (optional)
            </p>
          </div>
        ) : (
          <div className="bg-blue-50/30 p-3 rounded-lg border border-blue-200">
            <div className="relative inline-block">
              <img 
                src={currentLogo.preview} 
                alt="Current logo"
                className="h-32 w-auto object-contain rounded-lg border border-blue-200 bg-white p-2"
              />
              <button
                type="button"
                aria-label="Remove current logo"
                title="Remove current logo"
                className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
                onClick={removeCurrentLogo}
              >
                <X className="w-3 h-3" />
              </button>
              <span className="absolute -bottom-2 left-0 right-0 text-center">
                <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">Reference Only</span>
              </span>
            </div>
          </div>
        )}
      </div>
      
      {/* New Logo Concepts Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            New Logo Concepts to Test
          </label>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={triggerFileInput}
            aria-label="Add logos"
            title="Add logos"
          >
            <Upload className="w-4 h-4 mr-1" /> Add Logo Concepts
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            accept={ACCEPTED_IMAGE_INPUT}
            multiple
            className="hidden"
            aria-label="Upload logos"
            title="Upload logos"
          />
        </div>
        
        {images.length === 0 ? (
          <div 
            className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
            onClick={triggerFileInput}
          >
            <Upload className="w-8 h-8 mx-auto text-gray-400" />
            <p className="mt-2 text-sm text-gray-500">
              Click to upload or drag and drop your new logo concepts
            </p>
            <p className="text-xs text-gray-400">
              {UPLOAD_HINT_TEXT} • Maximum 5 logo concepts to test
            </p>
          </div>
        ) : (
          <div className="bg-purple-50/30 p-3 rounded-lg border border-purple-100">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {images.map((image, index) => (
                <div key={image.id} className="relative">
                  <img 
                    src={image.preview} 
                    alt={`Logo Concept ${String.fromCharCode(65 + index)}`}
                    className="h-32 w-full object-contain rounded-lg border border-purple-200 bg-white p-2"
                  />
                  <button
                    type="button"
                    aria-label={`Remove logo concept`}
                    title={`Remove logo concept`}
                    className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1 text-white hover:bg-red-600"
                    onClick={() => removeImage(image.id)}
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1 text-center">
                    <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                      Concept {String.fromCharCode(65 + index)}
                    </span>
                  </div>
                </div>
              ))}
              
              {images.length < 5 && (
                <div 
                  className="h-32 border-2 border-dashed border-purple-300 rounded-lg bg-white flex flex-col items-center justify-center cursor-pointer hover:border-purple-500 transition-colors"
                  onClick={triggerFileInput}
                >
                  <Upload className="w-6 h-6 text-purple-400" />
                  <span className="mt-1 text-xs text-purple-600">Add more</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      <div className="flex justify-between items-center">
        <div>
          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          disabled={!isFormValid || isSubmitting}
        >
          {isSubmitting ? 'Running Logo Test...' : 'Run Logo Test'}
        </Button>
      </div>
    </div>
  );
};

export default LogoTest;